/**
 * EvidenceModal Component
 * Modal for uploading evidence photos for a finding.
 *
 * Three evidence methods:
 * 1. Direct Upload — drag-drop or file picker (always available)
 * 2. Request Inspection — generates a finding-scoped secure link for winery manager
 * 3. Mobile Inspector Sim — demo-only simulated capture (gated behind isDemo)
 */

import { useState, useCallback, useRef } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { useDemoRole } from '../contexts/DemoRoleContext';
import {
  useCreateFindingLink,
  useFindingLinkStatus,
  getSecureLinkURL,
  copyToClipboard,
} from '../hooks/useSecureLinks';
import type { Finding } from '../types/api';

export interface EvidenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  finding: Finding;
  onRequestInspection?: () => void;
  onSubmit?: (files: File[]) => void;
}

type UploadMethod = null | 'direct' | 'inspection' | 'mobile';

export function EvidenceModal({
  isOpen,
  onClose,
  finding,
  onRequestInspection,
  onSubmit,
}: EvidenceModalProps) {
  const [method, setMethod] = useState<UploadMethod>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [linkUuid, setLinkUuid] = useState<string | undefined>(undefined);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { isDemo } = useDemoRole();
  const createFindingLink = useCreateFindingLink();
  const linkStatus = useFindingLinkStatus(linkUuid);

  const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/heic'];
  const MAX_SIZE = 10 * 1024 * 1024; // 10MB

  const handleFiles = useCallback((newFiles: FileList | File[]) => {
    const validFiles: File[] = [];
    const newPreviews: string[] = [];

    Array.from(newFiles).forEach((file) => {
      if (!ACCEPTED_TYPES.includes(file.type) && !file.name.toLowerCase().endsWith('.heic')) {
        return;
      }
      if (file.size > MAX_SIZE) {
        return;
      }
      validFiles.push(file);
      newPreviews.push(URL.createObjectURL(file));
    });

    setFiles((prev) => [...prev, ...validFiles]);
    setPreviews((prev) => [...prev, ...newPreviews]);
  }, []);

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleSubmit = () => {
    onSubmit?.(files);
    resetAndClose();
  };

  const handleRequestInspection = () => {
    onRequestInspection?.();
    resetAndClose();
  };

  const handleGenerateLink = async () => {
    try {
      const result = await createFindingLink.mutateAsync({
        policyId: finding.policyId,
        findingId: finding.id,
      });
      setLinkUuid(result.uuid);
    } catch {
      // Error handled by mutation state
    }
  };

  const handleCopyLink = async () => {
    if (!linkUuid) return;
    const url = getSecureLinkURL(linkUuid);
    const success = await copyToClipboard(url);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const resetAndClose = () => {
    setMethod(null);
    setFiles([]);
    setLinkUuid(undefined);
    setCopied(false);
    previews.forEach(URL.revokeObjectURL);
    setPreviews([]);
    onClose();
  };

  // Determine link display status
  const getLinkStatusBadge = () => {
    if (!linkUuid) return null;
    const data = linkStatus.data;
    if (!data) return { label: 'Pending', color: 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300' };
    if (data.status === 'completed') return { label: 'Completed', color: 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300' };
    if ((data.uploadCount ?? 0) > 0) return { label: 'In Progress', color: 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300' };
    return { label: 'Awaiting Access', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' };
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      title="Upload Evidence"
      description={`Evidence for: ${finding.title}`}
      size="lg"
      data-testid="evidence-modal"
      footer={
        method === 'direct' && files.length > 0 ? (
          <>
            <Button variant="secondary" onClick={resetAndClose}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit}>
              Upload {files.length} Photo{files.length !== 1 ? 's' : ''}
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={resetAndClose}>Cancel</Button>
        )
      }
    >
      {/* Method selection */}
      {!method && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" role="group" aria-label="Evidence upload methods">
          {/* Direct Upload */}
          <button
            onClick={() => setMethod('direct')}
            className="p-6 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg hover:border-gray-400 dark:hover:border-slate-500 hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition-colors text-left"
            aria-label="Upload photos directly from your device"
          >
            <svg className="w-8 h-8 text-gray-500 dark:text-slate-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100 mb-1">Upload Photos</h3>
            <p className="text-xs text-gray-500 dark:text-slate-400">
              Select files from your device. JPEG, PNG, HEIC up to 10MB.
            </p>
          </button>

          {/* Request On-Site Inspection */}
          <button
            onClick={() => setMethod('inspection')}
            className="p-6 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg hover:border-gray-400 dark:hover:border-slate-500 hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition-colors text-left"
            aria-label="Request on-site inspection via secure link"
          >
            <svg className="w-8 h-8 text-gray-500 dark:text-slate-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100 mb-1">Request Inspection</h3>
            <p className="text-xs text-gray-500 dark:text-slate-400">
              Generate a secure link for on-site GPS-tagged photo capture.
            </p>
          </button>

          {/* Mobile Inspector Simulator — demo only */}
          {isDemo && (
            <button
              onClick={handleRequestInspection}
              className="p-6 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg hover:border-gray-400 dark:hover:border-slate-500 hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition-colors text-left relative"
              aria-label="Launch mobile inspector simulator (demo only)"
            >
              <span className="absolute top-2 right-2 text-[9px] font-bold bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 px-1.5 py-0.5 rounded">
                DEMO
              </span>
              <svg className="w-8 h-8 text-gray-500 dark:text-slate-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100 mb-1">Mobile Simulator</h3>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                Simulated mobile inspector for demo purposes.
              </p>
            </button>
          )}
        </div>
      )}

      {/* Request Inspection sub-view */}
      {method === 'inspection' && (
        <div className="space-y-4">
          {!linkUuid ? (
            <div className="text-center py-6">
              <svg className="w-12 h-12 text-gray-500 dark:text-slate-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100 mb-1">
                Generate Inspection Link
              </h3>
              <p className="text-xs text-gray-500 dark:text-slate-400 mb-4 max-w-xs mx-auto">
                Creates a secure, time-limited link for the winery manager to upload
                GPS-tagged photos for this specific finding.
              </p>
              <Button
                variant="primary"
                onClick={handleGenerateLink}
                disabled={createFindingLink.isPending}
              >
                {createFindingLink.isPending ? 'Generating...' : 'Generate Link'}
              </Button>
              {createFindingLink.isError && (
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-2">Failed to generate link. Try again.</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Link URL display */}
              <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-700 dark:text-slate-300">Inspection Link</span>
                  {(() => {
                    const badge = getLinkStatusBadge();
                    return badge ? (
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${badge.color}`}>
                        {badge.label}
                      </span>
                    ) : null;
                  })()}
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-white dark:bg-slate-900 border dark:border-slate-600 rounded px-2 py-1.5 font-mono truncate text-gray-600 dark:text-slate-400" data-testid="generated-link-url">
                    {getSecureLinkURL(linkUuid)}
                  </code>
                  <button
                    onClick={handleCopyLink}
                    className="shrink-0 px-3 py-1.5 bg-gray-900 text-white text-xs rounded hover:bg-gray-800 transition-colors"
                    aria-label="Copy inspection link to clipboard"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-gray-50 dark:bg-slate-800 shadow-sm shadow-gray-900/5 rounded-lg p-3">
                <p className="text-xs text-gray-800 dark:text-slate-200">
                  Share this link with the winery manager. They can upload photos from their
                  mobile device without needing an account. The link expires in 7 days.
                </p>
              </div>

              {/* Link details */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-gray-50 dark:bg-slate-800 rounded p-2">
                  <span className="text-gray-500 dark:text-slate-400 block">Finding</span>
                  <span className="text-gray-900 dark:text-slate-100 font-medium">{finding.title}</span>
                </div>
                <div className="bg-gray-50 dark:bg-slate-800 rounded p-2">
                  <span className="text-gray-500 dark:text-slate-400 block">Photos Uploaded</span>
                  <span className="text-gray-900 dark:text-slate-100 font-medium">{linkStatus.data?.uploadCount ?? 0}</span>
                </div>
              </div>
            </div>
          )}

          {/* Back button */}
          <button
            onClick={() => { setMethod(null); setLinkUuid(undefined); setCopied(false); }}
            className="text-xs text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300"
          >
            &larr; Back to upload methods
          </button>
        </div>
      )}

      {/* Direct upload area */}
      {method === 'direct' && (
        <div>
          {/* Drag and drop zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragging
                ? 'border-gray-400 dark:border-slate-500 bg-gray-50 dark:bg-slate-800'
                : 'border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500'
            }`}
          >
            <svg className="w-10 h-10 mx-auto text-gray-400 dark:text-slate-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-sm text-gray-600 dark:text-slate-400 mb-1">
              Drag and drop photos here, or <span className="text-gray-600 dark:text-slate-400 font-medium">browse</span>
            </p>
            <p className="text-xs text-gray-400 dark:text-slate-500">JPEG, PNG, HEIC - 10MB max per file</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.heic"
            multiple
            className="hidden"
            onChange={(e) => e.target.files && handleFiles(e.target.files)}
          />

          {/* Photo preview grid */}
          {files.length > 0 && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
              {previews.map((preview, index) => (
                <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-slate-700 group">
                  <img
                    src={preview}
                    alt={`Photo ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    className="absolute top-1 right-1 w-5 h-5 bg-gray-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs px-1 py-0.5 text-center">
                    {files[index].name.slice(0, 12)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Back button */}
          <button
            onClick={() => { setMethod(null); setFiles([]); previews.forEach(URL.revokeObjectURL); setPreviews([]); }}
            className="mt-3 text-xs text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300"
          >
            &larr; Back to upload methods
          </button>
        </div>
      )}
    </Modal>
  );
}

export default EvidenceModal;
